<template>
    <div class="user-table-container">
        <div class="toolbar">
            <a-input-search
                v-model:value="searchText"
                placeholder="Search by email"
                allow-clear
                class="toolbar-search"
                @search="onSearch"
            />
            <a-select
                v-model:value="statusFilter"
                class="toolbar-select"
                placeholder="Filter by status"
                @change="onStatusChange"
            >
                <a-select-option value="">All statuses</a-select-option>
                <a-select-option value="no_subscription">No subscription</a-select-option>
                <a-select-option value="pending">Pending</a-select-option>
                <a-select-option value="active">Active</a-select-option>
                <a-select-option value="suspended">Suspended</a-select-option>
                <a-select-option value="expired">Expired</a-select-option>
            </a-select>
        </div>

        <a-table
            :columns="columns"
            :data-source="filteredUsers"
            :loading="loading"
            :pagination="{ current: pagination.current, pageSize: pagination.pageSize, total: filteredUsers.length, showTotal: (t) => `${t} users` }"
            row-key="id"
            :scroll="{ x: 900 }"
            @change="handleTableChange"
        >
            <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'email'">
                    <a class="user-link" @click="viewDetails(record.id)">{{ record.email || 'No email' }}</a>
                </template>
                <template v-else-if="column.key === 'status'">
                    <span class="pill" :class="getStatusClass(record.status)">{{ statusLabel(record.status) }}</span>
                </template>
                <template v-else-if="column.key === 'startDate'">
                    {{ formatDate(record.startDate) || '–' }}
                </template>
                <template v-else-if="column.key === 'endDate'">
                    {{ formatDate(record.endDate) || '–' }}
                </template>
                <template v-else-if="column.key === 'action'">
                    <div class="action-buttons">
                        <a-button size="small" @click="viewDetails(record.id)">View</a-button>
                        <a-button
                            size="small"
                            :type="record.status === 'pending' ? 'primary' : 'default'"
                            @click="goToApproval(record)"
                        >
                            Review
                        </a-button>
                    </div>
                </template>
            </template>
        </a-table>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '@/firebase/config';
import dayjs from 'dayjs';

// A page of the table is 10 rows; this is how many users are fetched in total
const USERS_LIMIT = 1000;

const columns = [
    { title: 'Email', dataIndex: 'email', key: 'email', width: 260 },
    { title: 'Plan', dataIndex: 'selectedPlan', key: 'selectedPlan' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Price', dataIndex: 'totalPrice', key: 'totalPrice' },
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', sorter: (a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0) },
    { title: 'End Date', dataIndex: 'endDate', key: 'endDate' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: '', key: 'action', width: 150, fixed: 'right' }
];

export default {
    data() {
        return {
            columns,
            users: [],
            loading: false,
            searchText: '',
            statusFilter: '',
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0
            },
            lastDoc: null
        }
    },
    computed: {
        filteredUsers() {
            let result = [...this.users];
            
            if (this.searchText) {
                const searchLower = this.searchText.toLowerCase();
                result = result.filter(user => 
                    user.email?.toLowerCase().includes(searchLower)
                );
            }

            if (this.statusFilter) {
                result = result.filter(user => user.status === this.statusFilter);
            }

            return result;
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return '';
            return dayjs(date).format('YYYY-MM-DD HH:mm');
        },
        getStatusClass(status) {
            const classes = { pending: 'amber', active: 'green', suspended: 'rose', expired: '' };
            return classes[status] ?? 'blue';
        },
        statusLabel(status) {
            return (status || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
        },
        viewDetails(id) {
            this.$router.push(`/user/${id}`);
        },
        goToApproval(record) {
            this.$router.push(`/approve?userId=${record.id}&path=${record.paymentProof}`);
        },
        onSearch(value) {
            this.searchText = value;
        },
        onStatusChange(value) {
            this.statusFilter = value;
        },
        handleTableChange(pag) {
            this.pagination.current = pag.current;
        },
        async loadUsers() {
            this.loading = true;
            try {
                // Get all users with subscription data embedded
                const usersRef = collection(db, 'users');
                // No orderBy: Firestore leaves out documents that lack the ordered field. Sorted below, newest first.
                const usersQuery = query(usersRef, limit(USERS_LIMIT));
                const usersSnapshot = await getDocs(usersQuery);
                
                // Array to store processed user data
                const processedData = [];
                
                // Process each user
                for (const userDoc of usersSnapshot.docs) {
                    const userData = userDoc.data();
                    
                    // Check if user has subscription data
                    if (userData.subscriptionData) {
                        const subscriptionData = userData.subscriptionData;
                        
                        const combinedUserData = {
                            id: userDoc.id,
                            email: userData.email || userData.personalInfo?.email,
                            approvedAt: subscriptionData.approvedAt || null,
                            approvedBy: subscriptionData.approvedBy || null,
                            selectedPlan: subscriptionData.selectedPlan,
                            type: subscriptionData.type,
                            totalPrice: subscriptionData.totalPrice,
                            status: subscriptionData.status || userData.subscriptionStatus,
                            paymentProof: subscriptionData.paymentProof,
                            startDate: subscriptionData.startDate?.toDate ? subscriptionData.startDate.toDate() : subscriptionData.startDate,
                            endDate: subscriptionData.endDate?.toDate ? subscriptionData.endDate.toDate() : subscriptionData.endDate,
                            createdAt: subscriptionData.createdAt?.toDate ? subscriptionData.createdAt.toDate() : subscriptionData.createdAt
                        };

                        // Determine status priority: suspended > active > pending
                        if (userData.subscriptionStatus === 'suspended' || userData.suspendedAt || userData.suspendedBy) {
                            combinedUserData.status = 'suspended';
                        } else if (subscriptionData.status) {
                            combinedUserData.status = subscriptionData.status;
                        } else if (!combinedUserData.approvedBy && !combinedUserData.approvedAt) {
                            combinedUserData.status = 'pending';
                        }
                        
                        processedData.push(combinedUserData);
                    } else {
                        // User without subscription data
                        const userWithoutSub = {
                            id: userDoc.id,
                            email: userData.email || userData.personalInfo?.email,
                            selectedPlan: 'No Subscription',
                            type: 'N/A',
                            totalPrice: 'N/A',
                            status: 'no_subscription',
                            startDate: null,
                            endDate: null,
                            createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : userData.createdAt
                        };
                        
                        processedData.push(userWithoutSub);
                    }
                }

                // Newest sign-ups first; accounts with no date go last
                const time = (u) => {
                    const d = u.createdAt || u.startDate;
                    const t = d ? new Date(d).getTime() : 0;
                    return Number.isFinite(t) ? t : 0;
                };
                processedData.sort((a, b) => time(b) - time(a));
                this.users = processedData;
            } catch (error) {
                console.error('Error loading users:', error);
            } finally {
                this.loading = false;
            }
        }
    },
    created() {
        this.loadUsers();
    }
}
</script>

<style>
.toolbar-search { width: 300px; max-width: 100%; }
.toolbar-select { width: 200px; }
.user-link { color: var(--ink); font-weight: 500; cursor: pointer; }
.user-link:hover { color: var(--red); }
.action-buttons { display: flex; gap: 8px; justify-content: flex-end; }
</style>
